import { useFlags } from "launchdarkly-react-client-sdk";

import { useUser } from "src/contexts/user-context";
import { useAiFeatureSettings } from "src/hooks/use-ai-feature-settings";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useLocation } from "src/router";

/**
 * Base capability for the agentic experience: the `agenticHomepage` flag is on,
 * data agents are enabled (CDP-only workspaces keep the legacy experience), and
 * the org hasn't turned off the Agents AI feature (Settings → AI features).
 */
export function useAgenticExperienceEnabled(): boolean {
  const { agenticHomepage } = useFlags();
  const agentsEnabled = useAgentsEnabledFlag();
  const { agents: agentsFeatureEnabled } = useAiFeatureSettings();

  return Boolean(agenticHomepage) && agentsEnabled && agentsFeatureEnabled;
}

/**
 * Whether the post-onboarding agentic surfaces are active: the agentic experience is
 * enabled and onboarding is complete. Gates both the agentic Home at `/home` and the
 * Data Overview page at `/overview`.
 */
export function useAgenticHomeEnabled(): boolean {
  const isEnabled = useAgenticExperienceEnabled();
  const { isOnboarding } = useUser();

  return isEnabled && isOnboarding === false;
}

/**
 * Whether an agentic full-bleed gradient surface is currently on screen — the
 * agentic home (`/home`) or the redesigned onboarding page (the whole `onboarding/*`
 * route, including the source/destination creation subpaths).
 */
export function useIsAgenticSurfaceActive(): boolean {
  const isEnabled = useAgenticExperienceEnabled();
  const isAgenticHomeEnabled = useAgenticHomeEnabled();
  const { pathname } = useLocation();

  if (isAgenticHomeEnabled && pathname.endsWith("/home")) {
    return true;
  }

  return (
    isEnabled &&
    (pathname.endsWith("/onboarding") || pathname.includes("/onboarding/"))
  );
}
